import { DatePicker } from '@vwfs-bronson/bronson-react';
import { useFormikContext } from 'formik';
import { t } from 'i18next';
import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { FormField } from '..';
import { FormFieldPropsBase } from './fieldset.model';

interface DatePickerFormFieldProps extends FormFieldPropsBase {
  minDate?: Date | string;
  maxDate?: Date | string;
  dateFormat?: string;
}

export const DatePickerFormField: FunctionComponent<DatePickerFormFieldProps> = ({
  labelTranslationKey = '',
  placeholderTranslationKey = '',
  isDisabled,
  fieldName,
  testId,
  minDate,
  maxDate,
  dateFormat = 'd/m/Y',
  optional,
  isReadonly,
}) => {
  const { setFieldValue, setFieldTouched, values } = useFormikContext();
  const [datePickerInputId] = useState(() => `datePicker_${Math.random()}`);

  // Manually set placeholder attribute on the input element.
  // In modal flow the actual input can mount after this effect first runs.
  // With [datePickerInputId, placeholderTranslationKey], this effect usually runs only once,
  // so if input is null at that moment the placeholder assignment is missed.
  useEffect(() => {
    const input = document.getElementById(datePickerInputId) as HTMLInputElement | null;
    if (input && 'placeholder' in input) {
      input.placeholder = t(placeholderTranslationKey);
    }
    // Intentionally no dependency array here: in modal/portal flow the input can mount later,
    // so this effect must keep re-running until the input exists and gets its placeholder.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  const setValueTouchAndValidate = useCallback(
    async (value: string) => {
      await setFieldValue(fieldName, value);
      setFieldTouched(fieldName, true, true);
    },
    [fieldName, setFieldTouched, setFieldValue]
  );

  const forceCalendarOverlayMode = useCallback((flatpickrInstance: any) => {
    const calendarContainer = flatpickrInstance?.calendarContainer as HTMLElement | undefined;
    if (!calendarContainer) return;

    calendarContainer.classList.remove('static');
    calendarContainer.style.position = 'absolute';
    calendarContainer.style.zIndex = '9999';
  }, []);

  return (
    <FormField
      name={fieldName}
      type="other"
      testId={testId ?? fieldName}
      labelText={`${t(labelTranslationKey)}${optional ? t('formControls:optionalLabelAffix') : ''}`}
      isDisabled={isDisabled}
      render={(fieldProps) => (
        <DatePicker
          {...fieldProps}
          id={datePickerInputId}
          name={fieldName}
          testId={testId}
          maxDate={maxDate}
          minDate={minDate}
          dateFormat={dateFormat}
          locale="ie"
          mode="single"
          // Inside modal/portal rendering, flatpickr can end up in static/in-flow mode.
          // Then calendar DOM takes normal layout space and pushes content below (results table).
          // We force popup overlay mode here so calendar floats above modal content instead.
          flatpickrOptions={{
            inline: false,
            static: false,
            onReady: (_selectedDates, _dateStr, instance: any) => {
              forceCalendarOverlayMode(instance);
            },
            onOpen: (_selectedDates, _dateStr, instance: any) => {
              forceCalendarOverlayMode(instance);
            },
          }}
          inputProps={{ readOnly: isReadonly }}
          value={(values as any)[fieldName] ?? ''}
          disabled={isDisabled}
          aria-disabled={isDisabled}
          // DatePickers' built-in placeholder will not work correctly
          // with it's own built-in input masking (based on dateFormat).
          // We want to use input masking so placeholder is set directly on input element.
          // Don't try setting placeholder like shown below:
          // placeholder={t(placeholderTranslationKey)}
          onChange={(e) => setValueTouchAndValidate(e.split('/').join('/'))}
          onBlur={(e) => setValueTouchAndValidate(e.split('/').join('/'))}
          onError={() => setValueTouchAndValidate('')}
        />
      )}
    />
  );
};
